import React, { useState, useEffect } from 'react';
import { X, Sparkle, CheckCircle, Spinner, TrendUp, Copy, RocketLaunch, Target, ArrowRight, Lightning, PencilSimple, Lightbulb } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';
import WhyThisFitsYouBlock from '@/components/WhyThisFitsYouBlock';
import { getUser } from '@/utils/auth';
import { useFormDefaults } from '@/store/usePayGBotContext';
import { useMissionHandoff } from '@/store/missionHandoffContext';
import { useWorkflowProgress } from '@/store/workflowProgress';
import {
  resolveBusinessModel,
  bridgeHeadingFor,
  recommendationsHeadingFor,
  showProductIdeasFor,
} from '@/utils/businessModel';
import OutputActions from './OutputActions';

const MonetizationPlanModal = ({ isOpen, onClose, onSuccess, onCreateContentPlan, onBuildOffer }) => {
  const user = getUser();
  const formDefaults = useFormDefaults();
  const workflowProgress = useWorkflowProgress();
  
  const [step, setStep] = useState('input'); // 'input', 'generating', 'result'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // Round 32 — niche-picker autocomplete.
  // Suggestion is derived from the typed business idea via
  // /api/business-systems/suggest. We debounce the lookup so every
  // keystroke doesn't fire a request, and we only render the
  // "Suggested business type: X" line when the backend returns a
  // canonical match (null response = no match, UI stays quiet).
  const [nicheSuggestion, setNicheSuggestion] = useState(null);
  // Tracks whether the user has already accepted the current
  // suggestion — once accepted we hide the pill until they edit again.
  const [suggestionAccepted, setSuggestionAccepted] = useState(false);
  
  const [formData, setFormData] = useState({
    // ─── Business-first intake (2026-05-01 Round 22) ───
    // Five ordered sections map to the existing backend contract so we
    // don't have to migrate `/api/monetization/plan`. The backend's
    // Pydantic model ignores unknown fields by default, so the new
    // business_stage / business_idea / business_interest meta-fields
    // are also sent and available for future prompt tuning.
    business_stage: 'idea',           // already_have | idea | scratch | not_sure
    business_idea: '',                // free-text, optional
    customer_stage: '0-100',          // maps 1:1 to backend current_audience_size
    time_available: '5-10 hrs/week',
    business_interest: [],            // maps to backend `skills` array
    goal_income: '$1000/mo',

    // Legacy passthrough — kept so the backend call site stays identical.
    niche: '',
    content_platform: 'tiktok',
  });

  // Round 108 — mission handoff prefill.
  const { handoff: missionHandoff, pickField: pickMissionField } = useMissionHandoff();
  const isMissionMode = !!(missionHandoff && missionHandoff.asset_id
    && (missionHandoff.asset_type === 'business_plan' || !missionHandoff.asset_type));

  useEffect(() => {
    if (!isOpen || !isMissionMode) return;
    const missionIdea = pickMissionField('summary', 'body', 'title', 'headline');
    if (missionIdea && typeof missionIdea === 'string') {
      setFormData(prev => ({ ...prev, business_idea: missionIdea, business_stage: 'already_have' }));
    }
    const missionNiche = missionHandoff.niche;
    if (missionNiche && typeof missionNiche === 'string') {
      setFormData(prev => ({ ...prev, niche: missionNiche }));
    }
    const missionPlatform = pickMissionField('preferred_platform', 'platform', 'recommended_platform');
    if (missionPlatform && typeof missionPlatform === 'string') {
      setFormData(prev => ({ ...prev, content_platform: missionPlatform.toLowerCase() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMissionMode]);

  // Auto-fill from global business context. Business Memory's `niche` is
  // used as a hint only when the user hasn't typed a business idea — we
  // never clobber a free-text entry the user made in this session.
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        niche: prev.niche || formDefaults.niche || '',
        business_idea: prev.business_idea || (formDefaults.niche ? '' : ''),
        content_platform: formDefaults.platform || prev.content_platform
      }));
    }
  }, [isOpen, formDefaults.niche, formDefaults.platform]);

  // Round 32 — debounced niche suggester.
  // Calls /api/business-systems/suggest 350ms after the user stops
  // typing. Silent on errors — the suggestion is a nice-to-have, not
  // a blocker for onboarding. Resets `suggestionAccepted` so users
  // who edit their idea after accepting get a fresh suggestion if the
  // new text maps to a different niche.
  useEffect(() => {
    const idea = (formData.business_idea || '').trim();
    if (!idea || idea.length < 3) {
      setNicheSuggestion(null);
      setSuggestionAccepted(false);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(
          `/business-systems/suggest?idea=${encodeURIComponent(idea)}`
        );
        if (!cancelled) {
          setNicheSuggestion(res?.data?.niche ? res.data : null);
          setSuggestionAccepted(false);
        }
      } catch (err) {
        if (!cancelled) setNicheSuggestion(null);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.business_idea]);

  const audienceSizes = [
    // Stays 4 tiers so it maps 1:1 to backend `current_audience_size`
    // ("0-100", "100-1000", "1000-10000", "10000+"). The labels now
    // cover clients / customers / followers so local / service / product
    // businesses don't feel excluded.
    { id: '0-100', label: '0', desc: 'Just starting' },
    { id: '100-1000', label: '1–10', desc: 'First clients' },
    { id: '1000-10000', label: '11–100', desc: 'Growing' },
    { id: '10000+', label: '100+', desc: 'Established' }
  ];

  const businessStages = [
    // New first question. Stored locally as business_stage so the UI
    // can steer copy (e.g. "I already have a business" users skip
    // business-idea encouragement). Also sent as metadata on submit.
    { id: 'already_have', label: 'I already have a business', desc: 'Active & running' },
    { id: 'idea', label: 'I have an idea', desc: 'Ready to validate' },
    { id: 'scratch', label: "I'm starting from scratch", desc: 'Figuring it out' },
    { id: 'not_sure', label: "I'm not sure yet", desc: "That's OK — we'll help" }
  ];

  const businessInterests = [
    // Replaces the old "skills" grid. Maps 1:1 to backend `skills` so
    // the existing prompt pipeline works unchanged — we just speak the
    // user's language (service / product / online / local / content /
    // coaching) instead of a grab-bag of verbs.
    { id: 'service', label: 'Service business', desc: 'Cleaning, repair, beauty' },
    { id: 'product', label: 'Product business', desc: 'Crafts, food, merch' },
    { id: 'online', label: 'Online business', desc: 'Digital products, SaaS' },
    { id: 'local', label: 'Local business', desc: 'Catering, tutoring' },
    { id: 'content', label: 'Content / influencer', desc: 'Creator, brand deals' },
    { id: 'coaching', label: 'Coaching / consulting', desc: 'Teaching, advising' },
    { id: 'other', label: 'Other', desc: 'Something else' }
  ];

  const timeOptions = [
    { id: '1-5 hrs/week', label: '1-5 hrs/week', desc: 'Side project' },
    { id: '5-10 hrs/week', label: '5-10 hrs/week', desc: 'Part-time' },
    { id: '10+ hrs/week', label: '10+ hrs/week', desc: 'Serious focus' }
  ];


  const incomeGoals = [
    { id: '$500/mo', label: '$500/mo' },
    { id: '$1000/mo', label: '$1000/mo' },
    { id: '$5000/mo', label: '$5000/mo' },
    { id: '$10000+/mo', label: '$10K+/mo' }
  ];

  // ──────────────────────────────────────────────────────────
  // Round 24 — "Because you said…" context pill.
  //
  // Surfaces the user's intake answers above the generated roadmap so
  // they can see PayGBot understood them. Pure UI helper — derived from
  // formData state, no extra network calls. Falls back gracefully when
  // the user skipped the new fields (e.g. legacy session) so the pill
  // simply doesn't render rather than printing "Because you said…".
  // ──────────────────────────────────────────────────────────
  const buildContextPill = () => {
    const stage = formData.business_stage;
    const ideaRaw = (formData.business_idea || '').trim();
    const isBlankIdea = !ideaRaw
      || /^(i\s?don'?t\s?know( yet)?|not sure|idk|unsure)$/i.test(ideaRaw);
    const interests = formData.business_interest || [];
    const interestLabel = (id) => (
      businessInterests.find((b) => b.id === id)?.label.toLowerCase() || id
    );
    const primaryInterest = interests[0];

    // Round 25 — multi-interest blend.
    // When the user picked 2+ interests, list them in a natural English
    // form ("A, B, and C") instead of dropping the extras. Helps the pill
    // feel like PayGBot heard *all* their answers, not just the first
    // checkbox they ticked.
    const formatInterestList = (ids) => {
      const labels = ids.map(interestLabel);
      if (labels.length === 0) return '';
      if (labels.length === 1) return labels[0];
      if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
      // 3+ → Oxford comma list. Caps at 4 items so the pill stays readable
      // on mobile (the +N tail is rare — only "other" is left if 5+).
      const head = labels.slice(0, 3);
      const rest = labels.length - 3;
      const tail = rest > 0 ? `, and ${rest} more` : '';
      return `${head.slice(0, -1).join(', ')}, and ${head[head.length - 1]}${tail}`;
    };
    const interestList = formatInterestList(interests);
    const isBlend = interests.length >= 2;

    // Most specific stage → least specific. We pick a single sentence so
    // the pill stays compact on mobile (one line on iPhone SE).
    if (stage === 'already_have') {
      if (ideaRaw && !isBlankIdea) {
        return `Because you already have a ${ideaRaw.toLowerCase()}, this plan focuses on scaling.`;
      }
      return 'Because you already have a business, this plan focuses on scaling.';
    }
    if (stage === 'idea') {
      if (ideaRaw && !isBlankIdea) {
        return `Because you have an idea (${ideaRaw}), this plan focuses on validating and launching it fast.`;
      }
      return 'Because you have an idea, this plan focuses on validating and launching fast.';
    }
    if (stage === 'scratch') {
      if (isBlend) {
        return `Because you're starting from scratch, PayGBot blended your interests in ${interestList} into a beginner-friendly direction.`;
      }
      if (primaryInterest) {
        return `Because you're starting from scratch, PayGBot picked a beginner-friendly ${interestLabel(primaryInterest)} direction.`;
      }
      return "Because you're starting from scratch, PayGBot picked a beginner-friendly direction.";
    }
    if (stage === 'not_sure') {
      if (isBlend) {
        return `Because you're not sure yet, PayGBot blended your interests in ${interestList} into a beginner-friendly direction.`;
      }
      if (primaryInterest) {
        return `Because you're not sure yet, PayGBot picked a beginner-friendly ${interestLabel(primaryInterest)} direction.`;
      }
      return "Because you're not sure yet, PayGBot picked a beginner-friendly direction.";
    }
    // No stage chosen (legacy session) — fall back to interest-only context.
    if (isBlend) {
      return `Because you're interested in ${interestList}, this plan blends them.`;
    }
    if (primaryInterest) {
      return `Because you're interested in ${interestLabel(primaryInterest)}, this plan is built around that.`;
    }
    return null;
  };

  // "Change my answers" — return to intake step WITHOUT clearing formData
  // so the user sees their previous selections pre-filled, edits what
  // they want, and re-submits. We also clear `result` so the stale
  // roadmap is dropped (otherwise scrolling to top would still show the
  // old plan briefly).
  const handleChangeAnswers = () => {
    setStep('input');
    setResult(null);
  };

  const toggleInterest = (id) => {
    setFormData((prev) => ({
      ...prev,
      business_interest: prev.business_interest.includes(id)
        ? prev.business_interest.filter((x) => x !== id)
        : [...prev.business_interest, id]
    }));
  };

  const handleSubmit = async () => {
    // Require at least one business-interest selection so the backend
    // `skills` array is never empty (its prompt expects concrete context).
    if (formData.business_interest.length === 0) {
      toast.error('Please pick at least one business type you are interested in.');
      return;
    }

    setStep('generating');
    setLoading(true);

    // Map the new business-first intake onto the existing backend
    // contract. Backend stays untouched per task scope. The spirit of
    // the rename is preserved: customer_stage → current_audience_size
    // (same tiers), business_interest → skills, business_idea → niche.
    // New fields (business_stage, business_idea, business_interest)
    // also go on the wire as metadata — Pydantic ignores unknown fields
    // by default so they survive the round-trip safely and can be used
    // by future prompt tuning without another frontend deploy.
    const idea = (formData.business_idea || '').trim();
    const resolvedNiche = idea || formData.niche || '';
    const payload = {
      // Existing backend contract (required) — semantically equivalent
      niche: resolvedNiche,
      current_audience_size: formData.customer_stage,
      content_platform: formData.content_platform,
      time_available: formData.time_available,
      skills: formData.business_interest,
      goal_income: formData.goal_income,
      // New business-first metadata — ignored today, usable tomorrow.
      business_stage: formData.business_stage,
      business_idea: idea,            // empty string = "I don't know yet"
      customer_stage: formData.customer_stage,
      business_interest: formData.business_interest,
    };

    // PART 4: retry up to 3 times with backoff + user-friendly errors
    const maxRetries = 3;
    const delays = [0, 2000, 5000];
    let lastError = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
        }
        const response = await api.post('/monetization/plan', payload, { timeout: 90000 });
        const planData = response.data;

        // Basic sanity check — backend should always return id + strategy
        if (!planData?.id && !planData?.recommended_strategy && !planData?.monetization_path) {
          throw new Error('Empty response');
        }

        setResult(planData);
        setStep('result');
        workflowProgress.completeIncomePath({ id: planData.id });
        if (onSuccess) onSuccess();
        toast.success('Your monetization plan is ready!');
        setLoading(false);
        return;
      } catch (error) {
        lastError = error;
        console.error(`[MonetizationPlan] attempt ${attempt + 1}/${maxRetries} failed:`, error?.response?.data || error.message);
      }
    }

    // All retries failed — show structured, user-friendly error + keep them in workflow
    setStep('input');
    setLoading(false);

    const status = lastError?.response?.status;
    const detail = lastError?.response?.data?.detail;
    let message;
    if (status === 422 || status === 400) {
      message = typeof detail === 'string' ? detail : 'We need a little more information to build your roadmap. Please fill in your niche and try again.';
    } else if (status === 504 || status === 520 || status === 522 || status === 524 || lastError?.code === 'ECONNABORTED') {
      // 520/522/524 = Cloudflare origin-unreachable cluster. Surface a
      // calmer, actionable message than a raw "network hiccup" so the
      // user understands the server is briefly unreachable, not their
      // connection. Regression fix 2026-04-30.
      message = 'The roadmap builder is briefly unreachable. Please try again in a moment — your inputs are saved.';
    } else if (status === 401 || status === 403) {
      message = 'Your session expired. Please refresh the page and sign in again.';
    } else if (!lastError?.response) {
      message = 'We couldn\'t reach the roadmap service right now. Please try again in a moment — your inputs are saved.';
    } else if (status >= 500) {
      message = 'The server hit a bump while building your roadmap. Please try again in a moment — your inputs are saved.';
    } else {
      message = "We couldn't create your roadmap right now. Please try again — your progress is safe.";
    }
    toast.error(message);
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleClose = () => {
    setStep('input');
    setResult(null);
    setFormData({
      business_stage: 'idea',
      business_idea: '',
      customer_stage: '0-100',
      time_available: '5-10 hrs/week',
      business_interest: [],
      goal_income: '$1000/mo',
      niche: '',
      content_platform: 'tiktok',
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-2xl paygbot-modal-shell bg-slate-900 rounded-2xl shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <TrendUp size={20} className="text-white" weight="fill" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Let's Plan Your Income</h2>
                <p className="text-sm text-slate-400">I'll create a roadmap just for you</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X size={18} className="text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Input Step — business-first intake (Round 22).
                Order: business stage → optional idea → client/customer
                count → weekly time → business interest → income goal. */}
            {step === 'input' && (
              <div className="space-y-6">
                {/* 1) Business stage */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Do you already have a business or idea?
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {businessStages.map((stage) => (
                      <button
                        key={stage.id}
                        onClick={() => setFormData({ ...formData, business_stage: stage.id })}
                        data-testid={`business-stage-${stage.id}`}
                        className={`p-3 rounded-lg text-left transition-all ${
                          formData.business_stage === stage.id
                            ? 'bg-green-500/20 border-green-500/50 border'
                            : 'bg-white/5 border-white/10 border hover:bg-white/10'
                        }`}
                      >
                        <span className={`block text-sm font-medium ${formData.business_stage === stage.id ? 'text-green-400' : 'text-white'}`}>
                          {stage.label}
                        </span>
                        <span className="block text-xs text-slate-500 mt-0.5">{stage.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 2) Optional business idea — free text.
                    Field is intentionally optional per spec: blank or
                    "I don't know yet" is a valid answer. When empty, the
                    submit maps to an empty niche and the backend prompt
                    still produces a beginner roadmap. */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Tell us about your business or idea
                  </label>
                  <p className="text-xs text-slate-500 mb-2">
                    Optional — you can type a business, idea, skill, or &ldquo;I don&rsquo;t know yet.&rdquo;
                  </p>
                  <textarea
                    value={formData.business_idea}
                    onChange={(e) => setFormData({ ...formData, business_idea: e.target.value.slice(0, 500) })}
                    placeholder="Example: cleaning service, digital products, catering, tutoring, fitness coaching, content creator, local service, etc."
                    rows={2}
                    data-testid="business-idea-input"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:border-green-500/50 focus:outline-none resize-none"
                  />
                  <p className="text-[10px] text-slate-500 mt-1 text-right">
                    {formData.business_idea.length}/500
                  </p>

                  {/* Round 32 — niche-picker autocomplete.
                      Shown only when the backend returned a canonical
                      match AND the user hasn't already accepted it.
                      Preserves the user's typed idea — we just pipe
                      the canonical niche into formData.niche so the
                      downstream bots get a clean signal. */}
                  {nicheSuggestion && nicheSuggestion.niche && !suggestionAccepted && (
                    <div
                      data-testid="niche-suggestion"
                      className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-green-500/30 bg-green-500/5 px-3 py-2"
                    >
                      <div className="text-xs text-slate-200 min-w-0">
                        <span className="text-slate-400">Suggested business type:</span>{' '}
                        <span
                          className="text-green-400 font-semibold capitalize"
                          data-testid="niche-suggestion-value"
                        >
                          {nicheSuggestion.niche}
                        </span>
                        {nicheSuggestion.mapping?.primary_platform && (
                          <span className="text-slate-500 hidden sm:inline">
                            {' '}· {nicheSuggestion.mapping.primary_platform}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        data-testid="use-niche-suggestion-btn"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            niche: nicheSuggestion.niche,
                          }));
                          setSuggestionAccepted(true);
                          toast.success(`Set business type to "${nicheSuggestion.niche}"`);
                        }}
                        className="shrink-0 text-xs font-medium text-green-300 bg-green-500/20 hover:bg-green-500/30 border border-green-500/40 rounded-md px-3 py-1 transition-colors"
                      >
                        Use this
                      </button>
                    </div>
                  )}
                  {suggestionAccepted && nicheSuggestion?.niche && (
                    <p
                      data-testid="niche-suggestion-accepted"
                      className="mt-2 text-[11px] text-green-400"
                    >
                      ✓ Using <span className="capitalize">{nicheSuggestion.niche}</span> as your business type.
                    </p>
                  )}
                </div>

                {/* 3) Customer / client / follower stage */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    How many clients, customers, or followers do you currently have?
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {audienceSizes.map((size) => (
                      <button
                        key={size.id}
                        onClick={() => setFormData({ ...formData, customer_stage: size.id })}
                        data-testid={`customer-stage-${size.id}`}
                        className={`p-3 rounded-lg text-center transition-all ${
                          formData.customer_stage === size.id
                            ? 'bg-green-500/20 border-green-500/50 border'
                            : 'bg-white/5 border-white/10 border hover:bg-white/10'
                        }`}
                      >
                        <span className={`text-sm font-medium ${formData.customer_stage === size.id ? 'text-green-400' : 'text-white'}`}>
                          {size.label}
                        </span>
                        <p className="text-xs text-slate-500 mt-1">{size.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 4) Time commitment (unchanged copy) */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    How much time can you dedicate each week?
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {timeOptions.map((time) => (
                      <button
                        key={time.id}
                        onClick={() => setFormData({ ...formData, time_available: time.id })}
                        className={`p-3 rounded-lg text-center transition-all ${
                          formData.time_available === time.id
                            ? 'bg-green-500/20 border-green-500/50 border'
                            : 'bg-white/5 border-white/10 border hover:bg-white/10'
                        }`}
                      >
                        <span className={`text-sm font-medium ${formData.time_available === time.id ? 'text-green-400' : 'text-white'}`}>
                          {time.label}
                        </span>
                        <p className="text-xs text-slate-500 mt-1">{time.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 5) Business interest — multi-select, replaces skills */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    What type of business are you interested in? <span className="text-slate-500 font-normal">(select all that apply)</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {businessInterests.map((bi) => (
                      <button
                        key={bi.id}
                        onClick={() => toggleInterest(bi.id)}
                        data-testid={`business-interest-${bi.id}`}
                        className={`px-3 py-2.5 rounded-lg text-left transition-all min-h-[52px] border ${
                          formData.business_interest.includes(bi.id)
                            ? 'bg-green-500/20 border-green-500/50 text-green-400'
                            : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                        }`}
                      >
                        <span className="block text-sm font-medium">{bi.label}</span>
                        <span className="block text-[11px] text-slate-500 mt-0.5">{bi.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 6) Income goal (unchanged) */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    What&apos;s your income goal?
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {incomeGoals.map((goal) => (
                      <button
                        key={goal.id}
                        onClick={() => setFormData({ ...formData, goal_income: goal.id })}
                        className={`p-3 rounded-lg text-center transition-all ${
                          formData.goal_income === goal.id
                            ? 'bg-green-500/20 border-green-500/50 border'
                            : 'bg-white/5 border-white/10 border hover:bg-white/10'
                        }`}
                      >
                        <span className={`text-sm font-medium ${formData.goal_income === goal.id ? 'text-green-400' : 'text-white'}`}>
                          {goal.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  data-testid="monetization-plan-submit"
                  className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold rounded-xl flex items-center justify-center space-x-2 transition-all"
                >
                  <Sparkle size={20} weight="fill" />
                  <span>Create My Roadmap</span>
                </button>
              </div>
            )}

            {/* Generating Step */}
            {step === 'generating' && (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Spinner size={32} className="text-white animate-spin" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">I'm Building Your Roadmap</h3>
                <p className="text-slate-400">Creating a personalized plan to reach {formData.goal_income}...</p>
              </div>
            )}

            {/* Result Step */}
            {step === 'result' && result && (
              <div className="space-y-6">
                {/* Round 24 — "Because you said…" context pill.
                    Shows the user PayGBot understood their intake and
                    gives them a one-tap path back to the form to tweak
                    any answer. Only renders when buildContextPill returns
                    text (legacy sessions with no business_stage skip it). */}
                {(() => {
                  const ctx = buildContextPill();
                  if (!ctx) return null;
                  return (
                    <div
                      className="flex items-start sm:items-center justify-between gap-3 px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg flex-wrap"
                      data-testid="roadmap-context-pill"
                    >
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <Lightbulb size={14} weight="fill" className="text-blue-400 mt-0.5 flex-shrink-0" />
                        <p
                          className="text-blue-200 text-xs leading-snug break-words"
                          data-testid="roadmap-context-text"
                        >
                          {ctx}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleChangeAnswers}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-blue-300 hover:text-white hover:bg-blue-500/20 rounded-md transition-colors flex-shrink-0"
                        data-testid="roadmap-change-answers-btn"
                      >
                        <PencilSimple size={12} weight="bold" />
                        Change my answers
                      </button>
                    </div>
                  );
                })()}

                {/* Strategy */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30">
                  <Target size={24} className="text-green-400 mb-2" />
                  <p className="text-green-400 font-semibold mb-1">Here's My Strategy For You</p>
                  <p className="text-white">{result.recommended_strategy}</p>
                </div>

                {/* Round 91 — Why This Fits You partnership reasoning */}
                <WhyThisFitsYouBlock
                  department="money"
                  taskType="business_strategy"
                  recommendationId={result?.id || result?.plan_id || null}
                  recommendationSummary={result.recommended_strategy}
                  businessCtx={{
                    business_name: formData?.business_name || undefined,
                    primary_offer: formData?.niche || undefined,
                    target_audience: formData?.audience || undefined,
                    hours_per_week: formData?.time_available || undefined,
                  }}
                />

                {/* Monetization Path */}
                <div className="space-y-3">
                  <h4 className="text-white font-medium">Your Path to {formData.goal_income}</h4>
                  {result.monetization_path?.map((phase, i) => (
                    <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-green-400 font-bold text-sm">{i + 1}</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-green-400 font-medium">{phase.phase}</p>
                          <p className="text-white mt-1">{phase.action}</p>
                          <div className="flex items-center space-x-4 mt-2 text-sm">
                            <span className="text-slate-500">Timeline: <span className="text-slate-300">{phase.timeline}</span></span>
                          </div>
                          <p className="text-slate-400 text-sm mt-1">{phase.expected_result}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quick Wins */}
                <div className="space-y-2">
                  <h4 className="text-white font-medium flex items-center space-x-2">
                    <RocketLaunch size={18} className="text-amber-400" />
                    <span>Start With These Quick Wins</span>
                  </h4>
                  <ul className="space-y-2">
                    {result.quick_wins?.map((win, i) => (
                      <li key={i} className="flex items-start space-x-2 text-slate-300 bg-white/5 p-3 rounded-lg">
                        <CheckCircle size={18} className="text-green-400 mt-0.5 flex-shrink-0" />
                        <span>{win}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Product Ideas — Round 78 Trust Alignment.
                    The label + visibility now depend on the user's
                    business_model. Local services / 1:1 pros don't
                    sell SKUs — we hide the cards and rename the
                    section so the page stays personalized. */}
                {(() => {
                  const _model = resolveBusinessModel(
                    formData.business_idea || formData.niche || result?.niche
                  );
                  const _heading = recommendationsHeadingFor(_model);
                  const _showCards = showProductIdeasFor(_model);
                  if (!_heading) return null;
                  return (
                    <div className="space-y-2" data-testid="rec-block" data-business-model={_model}>
                      <h4 className="text-white font-medium" data-testid="rec-block-heading">
                        {_heading}
                      </h4>
                      {_showCards ? (
                        <div className="grid gap-3">
                          {result.product_ideas?.map((product, i) => (
                            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-white font-medium">{product.name}</span>
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  product.difficulty === 'easy' ? 'bg-green-500/20 text-green-400' :
                                  product.difficulty === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                                  'bg-red-500/20 text-red-400'
                                }`}>
                                  {product.difficulty}
                                </span>
                              </div>
                              <div className="flex items-center space-x-4 text-sm text-slate-400">
                                <span>{product.type}</span>
                                <span className="text-green-400">{product.price_range}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p
                          className="text-sm text-slate-400 bg-white/5 border border-white/10 rounded-xl p-4"
                          data-testid="rec-block-empty-state"
                        >
                          Local services and professional practices grow through
                          visibility, referrals, and follow-up — not SKU launches.
                          Your Week 1 priorities will guide you through those
                          specific customer acquisition plays.
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Content to Monetization Bridge — Round 78 dynamic.
                    Heading swaps based on business_model so a handyman
                    never sees "How to Turn Content Into Sales". */}
                {(() => {
                  const _model = resolveBusinessModel(
                    formData.business_idea || formData.niche || result?.niche
                  );
                  const _heading = bridgeHeadingFor(_model);
                  if (!_heading || !result.content_to_monetization_bridge) return null;
                  return (
                    <div
                      className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4"
                      data-testid="bridge-block"
                      data-business-model={_model}
                    >
                      <h4 className="text-blue-400 font-medium mb-2" data-testid="bridge-block-heading">
                        {_heading}
                      </h4>
                      <p className="text-slate-300 text-sm">{result.content_to_monetization_bridge}</p>
                    </div>
                  );
                })()}

                {/* Next Steps */}
                <div className="space-y-2">
                  <h4 className="text-white font-medium">What You Should Do Next</h4>
                  <ol className="space-y-2">
                    {result.next_steps?.map((step, i) => (
                      <li key={i} className="flex items-start space-x-3 text-slate-300 bg-white/5 p-3 rounded-lg">
                        <span className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 text-sm font-bold flex-shrink-0">
                          {i + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Output Actions - Copy, Download PDF, Save */}
                <div className="pt-4 border-t border-white/10">
                  <OutputActions
                    content={`BUSINESS BLUEPRINT\n\nNiche: ${formData.niche}\nPlatform: ${formData.content_platform}\nGoal: ${formData.goal_income}\n\nSTRATEGY:\n${result.recommended_strategy}\n\nMONETIZATION PATH:\n${result.monetization_path?.map((p, i) => `${i+1}. ${p.phase}: ${p.action}`).join('\n')}\n\nQUICK WINS:\n${result.quick_wins?.map(w => `• ${w}`).join('\n')}\n\nNEXT STEPS:\n${result.next_steps?.map((s, i) => `${i+1}. ${s}`).join('\n')}`}
                    pdfType="business_plan"
                    pdfData={{
                      niche: formData.niche,
                      target_audience: `${formData.customer_stage} clients/customers/followers, ${formData.time_available}`,
                      platform: formData.content_platform,
                      business_model: result.recommended_strategy,
                      income_plan: result.monetization_path?.map(p => `${p.phase}: ${p.action} (${p.timeline})`),
                      next_steps: result.next_steps
                    }}
                    saveType="business_plan"
                    saveTitle={`Business Blueprint - ${formData.niche}`}
                    saveContent={{
                      ...result,
                      niche: formData.niche,
                      platform: formData.content_platform,
                      goal_income: formData.goal_income
                    }}
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-3 pt-4">
                  {/* Primary CTA - Build Offer (Money Bot next step) */}
                  <button
                    onClick={() => {
                      // Pass the plan result so Build Offer gets the full context
                      onBuildOffer?.(result);
                      handleClose();
                    }}
                    data-testid="build-offer-btn"
                    className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center space-x-2"
                  >
                    <Target size={20} weight="fill" />
                    <span>Build My Offer</span>
                    <ArrowRight size={18} />
                  </button>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => { setStep('input'); setResult(null); }}
                      className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                    >
                      Create New Plan
                    </button>
                    <button
                      onClick={handleClose}
                      className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default MonetizationPlanModal;
